import styled from '@emotion/styled';
import {
  API_CATALOG_INFO_KEY,
  CURRENT_USER_KEY,
  PORTAL_SUMMARY_KEY,
  useGetCurrentUser,
  useGetCustomStyles,
  useGetDynamicPages,
  useGetStaticPages
} from 'api/hooks';
import { webAppAPI } from 'api/web-app';
import PlaceholderLogo from 'assets/defaultImages/CompanyLogo.svg';
import { ReactComponent as LogoutIcon } from 'assets/door-icon.svg';
import { ReactComponent as DropdownArrowIcon } from 'assets/dropdown-arrow.svg';
import { ReactComponent as APIKeysIcon } from 'assets/home-icon.svg';
import { ReactComponent as UserIcon } from 'assets/user-icon.svg';
import * as React from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { mutate, trigger } from 'swr';
import { colors } from '../styles/colors';
import './Header.css';
import { SoloButton } from './SoloButton';

type UserControlsProps = {
  open: boolean;
  linkColorOverride?: string;
  defaultTextColor?: string;
  primaryColor?: string;
};

const UserControlsModal = styled.div`
  position: absolute;
  top: 47px;
  left: calc(50% - 78px);
  border-radius: 10px;
  background: white;
  width: 156px;
  font-size: 16px;
  line-height: 19px;
  z-index: 2;
  transition: max-height 0.2s;
  max-height: ${(props: UserControlsProps) => (props.open ? `175px;` : '0px')};
  overflow: hidden;
  ${(props: UserControlsProps) =>
    !!props.defaultTextColor &&
    `
    color: ${props.defaultTextColor};
    
    svg {
      * {
        fill: ${props.defaultTextColor};

        &.doorA {
          stroke: ${props.defaultTextColor};
        }
      }
    }`}

  > div {
    border-top: 12px solid ${(props: UserControlsProps) => props.primaryColor || colors.seaBlue};
    padding: 14px 14px 11px;

    > div {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-bottom: 10px;
      cursor: pointer;
    }
  }
`;

export const Header = () => {
  const history = useHistory();
  const { currentUser, currentUserError } = useGetCurrentUser();
  const { customStyles, customStylesError, customStylesLoading } = useGetCustomStyles();
  const { staticPages, staticPagesError } = useGetStaticPages();
  const { dynamicPages, dynamicPagesError } = useGetDynamicPages();

  let [cardImage, setCardImage] = React.useState<JSX.Element>(
    <img
      className='w-10 h-10 rounded-lg'
      src={customStyles?.logoUrl}
      alt=''
      onError={() => setCardImage(<img className='w-10 h-10 rounded-lg' src={PlaceholderLogo} alt='' />)}
    />
  );

  React.useEffect(() => {
    setCardImage(
      <img
        className='w-10 h-10 rounded-lg'
        src={customStyles?.logoUrl}
        alt=''
        onError={() => setCardImage(<img className='w-10 h-10 rounded-lg' src={PlaceholderLogo} alt='' />)}
      />
    );
  }, [customStylesLoading]);

  const [dropdownOut, setDropdownOut] = React.useState(false);

  const isApisSection = (pathname: string) => {
    return pathname.includes('/apis/');
  };

  const goToLogin = () => {
    history.push({
      pathname: '/login/'
    });
  };

  const goToAPIKeys = () => {
    history.push({
      pathname: '/keys/'
    });
  };

  const attemptLogout = () => {
    webAppAPI
      .logout()
      .then(result => {
        mutate(CURRENT_USER_KEY, '');
        mutate(API_CATALOG_INFO_KEY, undefined);
        mutate(PORTAL_SUMMARY_KEY, undefined);
        history.push('/');
      })
      .catch(err => console.error(err));
  };

  return (
    <div
      className='main-container-header'
      style={{
        ...(!!customStyles && {
          backgroundColor: `${customStyles.customStyling?.backgroundColor}`
        })
      }}>
      <div className='logo-holder'>{cardImage}</div>

      <div className='links-list'>
        <NavLink to={`/`} exact>
          Home
        </NavLink>
        <NavLink
          data-testid='navbar-apis'
          to={`/apis/`}
          isActive={(match, location) => isApisSection(location.pathname)}>
          APIs
        </NavLink>
        {staticPages?.map(page => (
          <NavLink to={page.path} key={page.path}>
            {page.navigationLinkName}
          </NavLink>
        ))}
        {dynamicPages?.map(page => (
          <NavLink to={page.path} key={page.path}>
            {page.navigationLinkName}
          </NavLink>
        ))}
        {currentUserError ? (
          <SoloButton text='Log In' onClick={goToLogin} />
        ) : (
          <div
            className={`header-user-controls ${!!dropdownOut ? 'is-open' : ''}`}
            onClick={() => setDropdownOut(o => !o)}>
            <div className={`header-user-control-button ${!!dropdownOut ? 'is-open' : ''}`}>
              <div className={`header-user-icon-container ${!!dropdownOut ? 'is-open' : ''}`}>
                <UserIcon />
              </div>
              {currentUser}
              <div className={`dropdown-arrow-container ${!!dropdownOut ? 'is-open' : ''} `}>
                <DropdownArrowIcon />
              </div>
            </div>

            <UserControlsModal
              className={`header-user-controls-modal ${!!dropdownOut ? 'is-open' : ''}`}
              style={{ ...(!!customStyles?.customStyling && { color: customStyles.customStyling?.defaultTextColor }) }}
              open={dropdownOut}
              linkColorOverride={customStyles?.customStyling?.secondaryColor}
              defaultTextColor={customStyles?.customStyling?.defaultTextColor}
              primaryColor={customStyles?.customStyling?.primaryColor}>
              <div>
                <div onClick={goToAPIKeys}>
                  API Keys
                  <div className='header-modal-icon-container '>
                    <APIKeysIcon />
                  </div>
                </div>
                <div onClick={attemptLogout}>
                  Log out
                  <div className='header-modal-icon-container '>
                    <LogoutIcon />
                  </div>
                </div>
              </div>
            </UserControlsModal>
          </div>
        )}
      </div>
    </div>
  );
};
